import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as R from 'ramda';

import TimeEntryStatusIcon from '@atom/components/common/timeEntryStatusIcon/TimeEntryStatusIcon';
import WorkOrderStatusPill from '@atom/components/common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import TeamContext from '@atom/components/teamPortal/TeamContext';
import {
  GET_TIME_ENTRIES_STATUS,
  TIME_ENTRY_UPDATE,
} from '@atom/graph/timeEntry';
import { GET_USER } from '@atom/graph/user';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useTeamNavigation } from '@atom/hooks/useTeamNavigation';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import {
  Icon,
  IconButton,
  ListTable,
  Progress,
  Select,
  Snackbar,
  TextField,
} from '@atom/mui';
import { userBudgetsSelector } from '@atom/selectors/userSelectors';
import { TimeEntryType } from '@atom/types/preferences';
import {
  TimeEntriesConnectionInput,
  TimeEntriesStatusConnection,
  TimeEntryDetail,
  TimeEntryStatus,
  TimeEntryUpdate,
  WorkOrderTimeEntry,
} from '@atom/types/timeEntry';
import { UserDetail, UserInput } from '@atom/types/user';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { timeSheetTableWidths } from '@atom/utilities/timeSheetUtilities';
import {
  convertHoursToMillis,
  convertMillisToExactHours,
  roundTimeEntryDuration,
  setDisplayDate,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './timeSheet.module.css';

const { TableRow, TableCell } = ListTable;
const { MenuItem } = Select;

const styles = {
  workInfo: {
    minWidth: timeSheetTableWidths.work,
  },
  smallCell: {
    padding: '8px 4px',
  },
  progress: {
    marginLeft: '12px',
  },
  start: {
    padding: '8px',
  },
  status: {
    margin: '12px 0',
  },
};

interface Props {
  timeEntry: TimeEntryDetail;
  refetchTimeSheet: () => void;
  setEditTimeEntryId: (id: string) => void;
  selectedPayPeriodWeek: number;
}

const TimeSheetTableEditRow = ({
  timeEntry,
  refetchTimeSheet,
  setEditTimeEntryId,
  selectedPayPeriodWeek,
}: Props) => {
  const [, { Link }] = useNavigation();

  const userProfile = useUserProfile();
  const preferences = usePreferences();
  const { teamNavigation } = useTeamNavigation();
  const { payPeriodWeeks } = teamNavigation;
  const { selectedPayPeriod } = useContext(TeamContext);
  const [date, setDate] = useState<any>(timeEntry.date || '');
  const [duration, setDuration] = useState<any>(
    convertMillisToExactHours(timeEntry.duration) || '',
  );
  const [selectedBudgetId, setSelectedBudgetId] = useState<string>(
    timeEntry?.budget?.id || '',
  );
  const [selectedType, setSelectedType] = useState<TimeEntryType>(
    timeEntry ? timeEntry?.type : null,
  );
  const [userGroupId, setUserGroupId] = useState<string>(
    timeEntry?.userGroupId || '',
  );
  const [isHandleTimeEntryUpdateLoading, setIsHandleTimeEntryUpdateLoading] =
    useState<boolean>(false);

  const [getUser, { loading: userLoading, data: userData }] = useLazyQuery<
    { user: UserDetail },
    { id: string; input: UserInput }
  >(GET_USER, {
    fetchPolicy:
      timeEntry.userId === userProfile?.userId ? 'cache-first' : 'network-only',
  });

  useEffect(() => {
    getUser({
      variables: {
        id: timeEntry.userId,
        input: {
          includeTimeEntryApproverInfo: true,
          includeRestoredBudgets: true,
        },
      },
    });
  }, [timeEntry.userId]);

  const [
    getTimeEntriesStatus,
    { loading: timeEntriesStatusLoading, data: timeEntriesStatusData },
  ] = useLazyQuery<
    { timeEntries: TimeEntriesStatusConnection },
    { input: TimeEntriesConnectionInput }
  >(GET_TIME_ENTRIES_STATUS, {
    fetchPolicy: 'network-only',
  });

  const [timeEntryUpdate] = useMutation<
    { timeEntryUpdate: WorkOrderTimeEntry },
    { input: TimeEntryUpdate }
  >(TIME_ENTRY_UPDATE, {
    onCompleted: () => {
      refetchTimeSheet();
    },
  });

  useEffect(() => {
    getTimeEntriesStatus({
      variables: {
        input: {
          userId: timeEntry.userId,
          userGroupId,
          dateStart: selectedPayPeriod?.startDate,
          dateEnd: selectedPayPeriod?.endDate,
        },
      },
    });
  }, [userGroupId]);

  const resetState = () => {
    setEditTimeEntryId(null);
    setDate('');
    setDuration('');
    setUserGroupId('');
    setIsHandleTimeEntryUpdateLoading(false);
  };

  const handleDuration = (newDuration: string, newDate: number) => {
    if (isNilOrEmpty(newDuration)) {
      setDuration('');
      setDate('');
    } else if (Number(newDuration) >= 0) {
      setDuration(newDuration);
      setDate(newDate);
    }
  };

  const onDurationBlur = () => {
    if (!isNilOrEmpty(duration)) {
      const roundedValue = roundTimeEntryDuration(duration);

      setDuration(roundedValue);
    }
  };

  const handleTimeEntryUpdate = async () => {
    setIsHandleTimeEntryUpdateLoading(true);

    try {
      await timeEntryUpdate({
        variables: {
          input: {
            id: timeEntry.id,
            date,
            userGroupId,
            duration: convertHoursToMillis(duration),
            ...(!preferences?.timeTracking?.computedBudgets &&
              !R.isEmpty(selectedBudgetId) && {
                budgetId: selectedBudgetId,
              }),
            ...(!isNilOrEmpty(preferences?.timeTracking?.typeEnumeration) && {
              type: selectedType,
            }),
          },
        },
      });

      resetState();
    } catch {
      Snackbar.error({ message: 'An unknown error occurred' });
    } finally {
      setIsHandleTimeEntryUpdateLoading(false);
    }
  };

  const currentWeekDates = payPeriodWeeks[selectedPayPeriodWeek] || [];

  const typeOptions = preferences?.timeTracking?.typeEnumeration;
  const hasTypeOptions = !isNilOrEmpty(typeOptions);

  const hideBudgetSelection = preferences?.timeTracking?.computedBudgets;
  const budgets = userBudgetsSelector(userData?.user);
  const budgetOptions = useMemo(() => {
    const options = budgets.filter(
      budget =>
        !budget.restored ||
        (budget.restored &&
          budget.reopenedWorkOrderIds.includes(timeEntry.workOrder.id)),
    );

    return [{ id: '', name: '', rate: 0 }, ...options];
  }, [budgets, timeEntry]);

  const userGroupIsApproved =
    timeEntriesStatusData?.timeEntries?.status === TimeEntryStatus.approved;

  const isSaveDisabled =
    isNilOrEmpty(date) ||
    isNilOrEmpty(duration) ||
    isNilOrEmpty(userGroupId) ||
    timeEntriesStatusLoading ||
    userGroupIsApproved ||
    (hasTypeOptions && R.isNil(selectedType));

  return (
    <>
      <TableRow key={timeEntry.id}>
        <TableCell
          width={timeSheetTableWidths.status}
          style={styles.start}
          center
        >
          <TimeEntryStatusIcon status={timeEntry.status} />
        </TableCell>
        <TableCell width={timeSheetTableWidths.workType}>
          {timeEntry?.workOrder?.workTemplate?.name}
        </TableCell>
        <TableCell style={styles.workInfo}>
          <div>
            <Link to={`/workOrders/${timeEntry?.workOrder?.id}`}>
              {timeEntry?.workOrder?.name}
            </Link>
            <WorkOrderStatusPill
              className="table"
              statusId={timeEntry?.workOrder?.statusId}
              style={styles.status}
            />
            {timeEntry?.workOrder?.inventoryAssetName && (
              <div
                className={classes.subtext}
              >{`Inventory: ${timeEntry?.workOrder?.inventoryAssetName}`}</div>
            )}
            {timeEntry?.workOrder?.dueDate && (
              <div className={classes.subtext}>{`Due Date: ${setDisplayDate(
                timeEntry?.workOrder?.dueDate,
              )}`}</div>
            )}
          </div>
        </TableCell>
        <TableCell width={timeSheetTableWidths.task}>
          {timeEntry?.task?.name}
        </TableCell>
        <TableCell width={timeSheetTableWidths.userGroup}>
          {!userLoading && (
            <Select
              value={userGroupId}
              error={userGroupIsApproved}
              helperText={
                userGroupIsApproved &&
                'Cannot add time to an approved timesheet.'
              }
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setUserGroupId(event.target.value)
              }
              data-cy="editTimeEntryGroupDropdown"
            >
              {userData?.user?.userGroups.map(userGroup => {
                const primaryText = userGroup.groupPath.join(' / ');

                return (
                  <MenuItem key={userGroup.id} value={userGroup.id}>
                    {primaryText}
                  </MenuItem>
                );
              })}
            </Select>
          )}
        </TableCell>
        <TableCell width={timeSheetTableWidths.budget}>
          {hideBudgetSelection
            ? hasTypeOptions && (
                <Select
                  fullWidth
                  value={selectedType}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setSelectedType(event.target.value as TimeEntryType)
                  }
                  data-cy="editTimeEntryBudgetDropdown"
                >
                  {typeOptions.map(type => {
                    const primaryText = type.name;

                    return (
                      <MenuItem key={type.value} value={type.value}>
                        <span className={classes.typeOption}>
                          {primaryText}
                        </span>
                      </MenuItem>
                    );
                  })}
                </Select>
              )
            : !userLoading && (
                <Select
                  fullWidth
                  value={selectedBudgetId}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setSelectedBudgetId(event.target.value)
                  }
                >
                  {budgetOptions.map(option => {
                    const primaryText = option.id ? (
                      <>
                        <span>
                          {`${option.name}: ${numberToLocaleString(
                            option.rate,
                          )}/Hours `}
                        </span>
                        {option.restored && (
                          <span className={classes.snapshotSuffix}>
                            (snapshot)
                          </span>
                        )}
                      </>
                    ) : (
                      'No Budget'
                    );

                    return (
                      <MenuItem key={option.id} value={option.id}>
                        {primaryText}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
        </TableCell>
        <TableCell
          width={timeSheetTableWidths.small}
          center
          style={styles.smallCell}
        />
        {currentWeekDates.map(day => {
          const showEdit =
            (isNilOrEmpty(date) && isNilOrEmpty(duration)) || date === day;

          return (
            <TableCell
              width={timeSheetTableWidths.small}
              center
              style={styles.smallCell}
              key={day}
            >
              {showEdit && (
                <TextField
                  type="text"
                  inputMode="numeric"
                  value={duration}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleDuration(event.target.value, day)
                  }
                  onBlur={onDurationBlur}
                />
              )}
            </TableCell>
          );
        })}
        <TableCell
          width={timeSheetTableWidths.small}
          center
          style={styles.smallCell}
        />
        <TableCell
          width={timeSheetTableWidths.action}
          style={styles.smallCell}
          center
        >
          <IconButton
            onClick={resetState}
            size="small"
            data-cy="timeEntryEditCloseButton"
          >
            <Icon>close</Icon>
          </IconButton>
        </TableCell>
        <TableCell
          width={timeSheetTableWidths.action}
          style={styles.smallCell}
          center
        >
          {timeEntriesStatusLoading || isHandleTimeEntryUpdateLoading ? (
            <Progress size={20} style={styles.progress} />
          ) : (
            <IconButton
              onClick={handleTimeEntryUpdate}
              disabled={isSaveDisabled}
              size="small"
              data-cy="timeEntryEditDoneButton"
            >
              <Icon>done</Icon>
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

export default TimeSheetTableEditRow;
